import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Edit, Building2, MapPin, Zap, FileText, CheckCircle, XCircle, Database } from 'lucide-react';
import backendApi from '../../services/backendApi';
import { clearSiteCache } from '../../components/PrivateHeader';
import { useAuth } from '../../context/AuthContext';
import {
  ModuleCatalogItem,
  loadModuleCatalog,
  getModuleSelectionMap,
  applySiteRolePolicies,
} from '../../utils/moduleAccessAdmin';


const SiteEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    site_name: '',
    site_code: '',
    site_type: '',
    location: '',
    capacity_mw: '',
    capacity_mwp: '',
    description: '',
    is_active: true,
    // New fields
    local_time_zone: '',
    timezone_preference: '',
    latitude: '',
    longitude: '',
    altitude: '',
    ppa_tariff: '',
    plant_code: '',
    currency: 'INR',
    plant_commissioning_date: '',
    data_resolution: '',
    site_manager_name: '',
    site_manager_email: '',
    site_manager_phone_no: '',
    company_id: '',
    database_name: ''
  });

  const { user } = useAuth();
  const isSystemLevelAdmin =
    !!user?.isGlobalAdmin ||
    user?.role === 'system_admin' ||
    user?.role === 'super_admin' ||
    user?.role === 'admin';
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogItem[]>([]);
  const [companyAllowedModuleKeys, setCompanyAllowedModuleKeys] = useState<string[]>([]);
  const [siteModuleSelection, setSiteModuleSelection] = useState<Record<string, boolean>>({});
  const [applySiteModulePolicy, setApplySiteModulePolicy] = useState(true);
  const [sitePolicyRoleIds, setSitePolicyRoleIds] = useState<number[]>([]);
  const [loadingModuleConfig, setLoadingModuleConfig] = useState(false);

  const availableSiteModules = moduleCatalog.filter(
    (module) => companyAllowedModuleKeys.includes(module.module_key)
  );


  useEffect(() => {
    if (id) {
      fetchSite();
    }
    fetchCompanies();
  }, [id, user, isSystemLevelAdmin]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [modules, rolesResponse] = await Promise.all([
          loadModuleCatalog(backendApi),
          backendApi.getRoles(),
        ]);

        setModuleCatalog(modules);
        setSiteModuleSelection(getModuleSelectionMap(modules));

        const roles = Array.isArray(rolesResponse?.roles) ? rolesResponse.roles : [];
        const roleIds = roles
          .filter((role: any) => ['site_admin', 'general_user'].includes(String(role?.role_name || '').toLowerCase()))
          .map((role: any) => Number(role?.role_id))
          .filter((value: number) => Number.isFinite(value) && value > 0);
        setSitePolicyRoleIds(roleIds);
      } catch (error) {
        console.error('Failed to load site edit module metadata:', error);
      }
    };

    loadMeta();
  }, []);

  // Auto-fill company_id fallback for non-super admins if site has none
  useEffect(() => {
    if (!formData.company_id && user?.companyId) {
      setFormData(prev => ({ ...prev, company_id: String(user.companyId) }));
    }
  }, [formData.company_id, user, isSystemLevelAdmin]);

  useEffect(() => {
    if (moduleCatalog.length === 0) {
      return;
    }

    if (!formData.company_id) {
      setCompanyAllowedModuleKeys([]);
      return;
    }

    void loadCompanyModuleAccess(formData.company_id);
  }, [moduleCatalog, formData.company_id]);

  useEffect(() => {
    if (!id || moduleCatalog.length === 0) {
      return;
    }

    void loadSitePolicyConfig(Number(id));
  }, [id, moduleCatalog]);

  const syncSelectionToAllowedKeys = (allowedKeys: string[]) => {
    setSiteModuleSelection((prev) => {
      const next: Record<string, boolean> = {};
      moduleCatalog.forEach((module) => {
        if (allowedKeys.includes(module.module_key)) {
          next[module.module_key] = prev[module.module_key] !== undefined ? prev[module.module_key] : true;
        }
      });
      return next;
    });
  };

  const loadCompanyModuleAccess = async (companyId: string | null) => {
    if (!companyId) {
      setCompanyAllowedModuleKeys([]);
      syncSelectionToAllowedKeys([]);
      return;
    }

    try {
      const response = await backendApi.getNavigationSubscriptions(companyId);
      if (!response.success) {
        setCompanyAllowedModuleKeys([]);
        syncSelectionToAllowedKeys([]);
        return;
      }

      const activeKeys: string[] = Array.from(new Set<string>(
        (response.subscriptions || [])
          .filter((item: any) => String(item?.status || '').toLowerCase() === 'active')
          .map((item: any) => String(item?.module_key || '').trim())
          .filter((key: string) => key.length > 0)
      ));

      setCompanyAllowedModuleKeys(activeKeys);
      syncSelectionToAllowedKeys(activeKeys);
    } catch (error) {
      console.error('Failed to load company module subscriptions for site edit:', error);
      setCompanyAllowedModuleKeys([]);
      syncSelectionToAllowedKeys([]);
    }
  };

  const loadSitePolicyConfig = async (siteId: number) => {
    if (!siteId) {
      return;
    }

    setLoadingModuleConfig(true);
    try {
      const response = await backendApi.getNavigationRolePolicies({
        siteId,
        scopeLevel: 'site',
      });

      const allowedKeys = new Set<string>();
      if (response?.success) {
        (response.allowed_module_keys || []).forEach((key: string) => {
          if (key) {
            allowedKeys.add(String(key).trim());
          }
        });

        (response.policies || []).forEach((policy: any) => {
          const permissionKey = String(policy?.permission_key || '').trim();
          if (
            permissionKey.endsWith('.view') &&
            String(policy?.effect || '').toLowerCase() === 'allow' &&
            policy?.is_active &&
            policy?.is_valid_now !== false
          ) {
            allowedKeys.add(permissionKey.replace(/\.view$/, ''));
          }
        });
      }

      setSiteModuleSelection(getModuleSelectionMap(moduleCatalog, Array.from(allowedKeys)));
    } catch (error) {
      console.error('Failed to load site policy config for edit form:', error);
    } finally {
      setLoadingModuleConfig(false);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await backendApi.getCompanies();
      if (response.success && response.companies) {
        setCompanies(response.companies);
      } else if (user?.companyId) {
        setCompanies([{
          company_id: user.companyId,
          company_name: user.company || 'Assigned Company',
          company_code: String(user.companyId)
        }]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (user?.companyId) {
        setCompanies([{
          company_id: user.companyId,
          company_name: user.company || 'Assigned Company',
          company_code: String(user.companyId)
        }]);
      }
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchSite = async () => {
    try {
      setLoading(true);
      const response = await backendApi.getSite(id, false); // Use regular endpoint
      if (response.success && response.site) {
        const site = response.site;
        setFormData({
          site_name: site.site_name || '',
          site_code: site.site_code || '',
          site_type: site.site_type || '',
          location: site.location || '',
          capacity_mw: site.capacity_mw ? String(site.capacity_mw) : '',
          capacity_mwp: site.capacity_mwp ? String(site.capacity_mwp) : '',
          description: site.description || '',
          is_active: site.is_active !== false,
          // New fields
          local_time_zone: site.local_time_zone || '',
          timezone_preference: site.timezone_preference || '',
          latitude: site.latitude ? String(site.latitude) : '',
          longitude: site.longitude ? String(site.longitude) : '',
          altitude: site.altitude ? String(site.altitude) : '',
          ppa_tariff: site.ppa_tariff ? String(site.ppa_tariff) : '',
          plant_code: site.plant_code !== null && site.plant_code !== undefined ? String(site.plant_code) : '',
          currency: site.currency || 'INR',
          plant_commissioning_date: site.plant_commissioning_date || '',
          data_resolution: site.data_resolution || '',
          site_manager_name: site.site_manager_name || '',
          site_manager_email: site.site_manager_email || '',
          site_manager_phone_no: site.site_manager_phone_no || '',
          company_id: site.company_id ? String(site.company_id) : '',
          database_name: site.database_name || ''
        });
      } else {

        alert(response.error || 'Failed to load site');
        navigate('/admin/sites');
      }
    } catch (error) {
      console.error('Error fetching site:', error);
      alert('Failed to load site');
      navigate('/admin/sites');
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.site_name.trim()) newErrors.site_name = 'Site name is required';
    if (!formData.site_code.trim()) newErrors.site_code = 'Site code is required';
    if (formData.capacity_mw && isNaN(Number(formData.capacity_mw))) {
      newErrors.capacity_mw = 'Capacity must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);
    try {
      const updateData: any = {
        site_name: formData.site_name.trim(),
        site_code: formData.site_code.trim(),
        site_type: formData.site_type.trim() || null,
        location: formData.location.trim() || null,
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        // New fields
        local_time_zone: formData.local_time_zone.trim() || formData.timezone_preference.trim() || null,
        timezone_preference: formData.timezone_preference.trim() || formData.local_time_zone.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        altitude: formData.altitude ? parseFloat(formData.altitude) : null,
        ppa_tariff: formData.ppa_tariff ? parseFloat(formData.ppa_tariff) : null,
        plant_code: formData.plant_code ? parseInt(formData.plant_code) : null,
        currency: formData.currency.trim() || 'INR',
        plant_commissioning_date: formData.plant_commissioning_date || null,
        data_resolution: formData.data_resolution.trim() || null,
        site_manager_name: formData.site_manager_name.trim() || null,
        site_manager_email: formData.site_manager_email.trim() || null,
        site_manager_phone_no: formData.site_manager_phone_no.trim() || null,
        company_id: formData.company_id || null,
        database_name: formData.database_name.trim() || undefined
      };


      if (formData.capacity_mw) {
        updateData.capacity_mw = parseFloat(formData.capacity_mw);
      }
      if (formData.capacity_mwp) {
        updateData.capacity_mwp = parseFloat(formData.capacity_mwp);
      }

      const response = await backendApi.updateSite(Number(id), updateData);

      if (response.success) {
        const updatedSiteId = Number(id);
        const updatedCompanyId = Number(formData.company_id);
        const constrainedSiteSelection = availableSiteModules.reduce((acc, module) => {
          acc[module.module_key] = !!siteModuleSelection[module.module_key];
          return acc;
        }, {} as Record<string, boolean>);

        if (
          applySiteModulePolicy &&
          updatedSiteId > 0 &&
          updatedCompanyId > 0 &&
          sitePolicyRoleIds.length > 0 &&
          Object.keys(constrainedSiteSelection).length > 0
        ) {
          await applySiteRolePolicies(backendApi, {
            companyId: updatedCompanyId,
            siteId: updatedSiteId,
            moduleSelections: constrainedSiteSelection,
            roleIds: sitePolicyRoleIds,
          });
        }

        // Clear site cache to force refresh of map coordinates in PrivateHeader
        if (formData.site_code) {
          clearSiteCache(formData.site_code.toUpperCase());
        }
        // Also clear all site caches to be safe
        clearSiteCache();
        navigate('/admin/sites');
      } else {
        alert(response.error || 'Failed to update site');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to update site');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading site...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Site configuration</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Edit size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Edit site: {formData.site_name || `#${id}`}</h1>
                  <p className="admin-page-subtitle mt-2">Update company mapping, technical fields, manager details, and status in the new admin layout.</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/admin/sites')}
              className="admin-button admin-button-ghost"
            >
              <ArrowLeft size={16} />
              Back to Sites
            </button>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-section"
        >
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Site edit form</h2>
              <p className="admin-section-copy">Maintain core details, geo and plant metadata, plus manager contact values for this site.</p>
            </div>
          </div>
          <div className="admin-section-body !p-4 sm:!p-5 lg:!p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Selection */}
            <div className="bg-[#F2F8FC]/50 p-4 rounded-lg border border-[#E3F0F9] mb-8">
              <label className="block text-sm font-semibold text-[#0D2747] mb-2 flex items-center gap-2">
                <Building2 size={18} className="text-[#0066A3]" />
                Assigned Company
              </label>
              <div className="relative">
                <select
                  value={formData.company_id}
                  onChange={(e) => {
                    const nextCompanyId = e.target.value;
                    setFormData({ ...formData, company_id: nextCompanyId });
                  }}
                  disabled={!isSystemLevelAdmin && !!user?.companyId}
                  className={`w-full px-4 py-3 border border-[#C2DFF1] rounded-lg focus:ring-2 focus:ring-[#0084CF] outline-none transition-all appearance-none bg-white font-medium ${!isSystemLevelAdmin && !!user?.companyId ? 'bg-[#E3F0F9]/60 cursor-not-allowed' : ''}`}
                >
                  <option value="">No Company (System Default)</option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.company_name} ({company.company_code})
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#0084CF]">
                  {loadingCompanies ? (
                    <div className="w-5 h-5 border-2 border-[#C2DFF1] border-t-blue-500 rounded-full animate-spin"></div>
                  ) : (
                    <ArrowLeft size={18} className="rotate-[270deg]" />
                  )}
                </div>
              </div>
              <p className="text-[#1C4D88]/70 text-xs mt-2">
                Associate this site with a specific company for data isolation.
              </p>
            </div>

            <div className="bg-white border border-[#E3F0F9] rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-xl font-black text-black">Assigned Modules</h3>
                  <p className="text-base font-extrabold text-gray-900">Edit module access for Site Admin and General User at this site.</p>
                </div>
                <label className="flex items-center gap-2 text-lg font-black text-black cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applySiteModulePolicy}
                    onChange={(e) => setApplySiteModulePolicy(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Apply on save
                </label>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    const allEnabled: Record<string, boolean> = {};
                    availableSiteModules.forEach((module) => {
                      allEnabled[module.module_key] = true;
                    });
                    setSiteModuleSelection((prev) => ({ ...prev, ...allEnabled }));
                  }}
                  className="px-3 py-1.5 text-sm font-black border-2 border-[#0066A3] text-[#1C4D88] rounded-lg hover:bg-[#F2F8FC]"
                  disabled={availableSiteModules.length === 0}
                >
                  Enable All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const allDisabled: Record<string, boolean> = {};
                    availableSiteModules.forEach((module) => {
                      allDisabled[module.module_key] = false;
                    });
                    setSiteModuleSelection((prev) => ({ ...prev, ...allDisabled }));
                  }}
                  className="px-3 py-1.5 text-sm font-black border-2 border-red-600 text-red-700 rounded-lg hover:bg-red-50"
                  disabled={availableSiteModules.length === 0}
                >
                  Disable All
                </button>
              </div>

              {loadingModuleConfig ? (
                <div className="text-sm text-gray-500 py-3">Loading assigned modules...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {availableSiteModules.map((module) => (
                    <label key={module.module_key} className="flex items-center justify-between gap-2 border border-gray-200 rounded-md px-3 py-2 bg-gray-50">
                      <span className="text-base font-black text-black">{module.module_name}</span>
                      <input
                        type="checkbox"
                        checked={!!siteModuleSelection[module.module_key]}
                        onChange={(e) => {
                          setSiteModuleSelection((prev) => ({
                            ...prev,
                            [module.module_key]: e.target.checked,
                          }));
                        }}
                      />
                    </label>
                  ))}
                </div>
              )}
              {availableSiteModules.length === 0 && !loadingModuleConfig && (
                <p className="text-base font-black text-amber-950 mt-2">No modules are currently assigned to this company. Assign modules at company level first.</p>
              )}
            </div>

            {/* Site Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Site Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.site_name}
                  onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.site_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="Enter site name"
                />
              </div>
              {errors.site_name && <p className="text-red-500 text-sm mt-1">{errors.site_name}</p>}
            </div>

            {/* Site Code */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Site Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.site_code}
                  onChange={(e) => setFormData({ ...formData, site_code: e.target.value.toUpperCase() })}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.site_code ? 'border-red-500' : 'border-gray-300'
                    }`}
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="Enter site code (e.g., AXON001)"
                />
              </div>
              {errors.site_code && <p className="text-red-500 text-sm mt-1">{errors.site_code}</p>}
            </div>

            {/* Dedicated Site Telemetry Database Configuration Card */}
            <div className="bg-gradient-to-r from-[#F2F8FC]/70 to-[#F2F8FC]/50 p-4 sm:p-5 rounded-xl border border-[#C2DFF1]/80 shadow-sm space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0066A3] text-white flex items-center justify-center shadow-sm">
                    <Database size={18} />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-900">
                      Site Telemetry Database
                    </label>
                    <p className="text-xs text-gray-600">
                      Dedicated SQL Server database for this site's telemetry and live analytics data.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <Database className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={formData.database_name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, database_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }));
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#C2DFF1] rounded-lg focus:ring-2 focus:ring-[#0084CF] bg-white text-gray-900 font-mono text-sm placeholder-gray-400"
                  placeholder="e.g. axonmetric_dattainfradevikotbhakrani"
                  maxLength={128}
                />
              </div>

              <div className="flex items-start gap-2 bg-[#E3F0F9]/60 rounded-lg p-2.5 text-xs text-[#0D2747]">
                <CheckCircle size={15} className="text-[#0066A3] shrink-0 mt-0.5" />
                <span>
                  Telemetry dashboards dynamically route queries to this database. If newly assigned, required tables (<code>axonmetric_livedaycons</code> and <code>axonmetric_livestringinv</code>) will be created automatically.
                </span>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Site Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Site Type
                </label>
                <input
                  type="text"
                  value={formData.site_type}
                  onChange={(e) => setFormData({ ...formData, site_type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="e.g., ACSEPL, WTC, LAP, AXON"
                />
              </div>

              {/* Capacity MW and MWp */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Capacity (MW) - AC
                  </label>
                  <div className="relative">
                    <Zap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.capacity_mw}
                      onChange={(e) => setFormData({ ...formData, capacity_mw: e.target.value })}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.capacity_mw ? 'border-red-500' : 'border-gray-300'
                        }`}
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder="Enter AC capacity"
                    />
                  </div>
                  {errors.capacity_mw && <p className="text-red-500 text-sm mt-1">{errors.capacity_mw}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Capacity (MWp) - DC
                  </label>
                  <div className="relative">
                    <Zap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.capacity_mwp}
                      onChange={(e) => setFormData({ ...formData, capacity_mwp: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder="Enter DC capacity"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="Enter site location/address"
                />
              </div>
            </div>



            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-4 text-gray-400" size={20} />
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent resize-none bg-white text-gray-900 placeholder-gray-500"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="Enter site description or notes"
                />
              </div>
            </div>

            {/* Active Status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Status
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: true })}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${formData.is_active
                    ? 'bg-green-100 text-green-700 border-2 border-green-300'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                >
                  <CheckCircle size={20} />
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: false })}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${!formData.is_active
                    ? 'bg-red-100 text-red-700 border-2 border-red-300'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                >
                  <XCircle size={20} />
                  Inactive
                </button>
              </div>
            </div>

            {/* New Fields Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Additional Site Information</h3>

              {/* Timezone Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Local Time Zone
                  </label>
                  <input
                    type="text"
                    value={formData.local_time_zone}
                    onChange={(e) => setFormData({ ...formData, local_time_zone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., Asia/Kolkata"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Timezone Preference
                  </label>
                  <input
                    type="text"
                    value={formData.timezone_preference}
                    onChange={(e) => setFormData({ ...formData, timezone_preference: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., IST"
                  />
                </div>
              </div>

              {/* Location Coordinates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="0.0000001"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., 28.6139"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="0.0000001"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., 77.2090"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Altitude (meters)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.altitude}
                    onChange={(e) => setFormData({ ...formData, altitude: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., 216"
                  />
                </div>
              </div>

              {/* Plant Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    PPA Tariff
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.ppa_tariff}
                    onChange={(e) => setFormData({ ...formData, ppa_tariff: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., 2.50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Plant Code
                  </label>
                  <select
                    value={formData.plant_code}
                    onChange={(e) => setFormData({ ...formData, plant_code: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Plant Type</option>
                    <option value="0">Solar</option>
                    <option value="1">Wind</option>
                    <option value="2">Hybrid</option>
                    <option value="3">Battery-Hydro</option>
                    <option value="4">Battery-Chemical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Currency
                  </label>
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder="e.g., INR"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Plant Commissioning Date
                  </label>
                  <input
                    type="date"
                    value={formData.plant_commissioning_date}
                    onChange={(e) => setFormData({ ...formData, plant_commissioning_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Data Resolution
                  </label>
                  <select
                    value={formData.data_resolution}
                    onChange={(e) => setFormData({ ...formData, data_resolution: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Resolution</option>
                    <option value="1m">1 minute</option>
                    <option value="5m">5 minutes</option>
                    <option value="10m">10 minutes</option>
                    <option value="15m">15 minutes</option>
                  </select>
                </div>
              </div>

              {/* Site Manager Information */}
              <div className="border-t pt-6 mt-6">
                <h4 className="text-md font-bold text-gray-900 mb-4">Site Manager Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site Manager Name
                    </label>
                    <input
                      type="text"
                      value={formData.site_manager_name}
                      onChange={(e) => setFormData({ ...formData, site_manager_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder="Manager Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site Manager Email
                    </label>
                    <input
                      type="email"
                      value={formData.site_manager_email}
                      onChange={(e) => setFormData({ ...formData, site_manager_email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder="manager@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site Manager Phone No.
                    </label>
                    <input
                      type="tel"
                      value={formData.site_manager_phone_no}
                      onChange={(e) => setFormData({ ...formData, site_manager_phone_no: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder="+91-1234567890"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin/sites')}
                className="admin-button admin-button-ghost flex-1 justify-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="admin-button admin-button-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default SiteEdit;

